import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './Familymemberdetails.css';

const COUPONS = [
  {
    key: 'coupon1',
    label: 'Test Coupon',
    validUntil: 'May 30, 2026',
    link: 'https://www.1mg.com/otc/deksel-oral-nano-liquid-60000-iu-5ml-each-sugar-free-otc634337',
    btnLabel: 'Redeem test',
  },
  {
    key: 'coupon2',
    label: 'Supplement Coupon',
    validUntil: 'Jun 30, 2026',
    link: 'https://www.1mg.com',
    btnLabel: 'Redeem supplement',
  },
];

function CouponCard({ coupon, coupons }) {
  const [copied, setCopied] = useState(false);
  const code = coupons?.[coupon.key];

  if (!code) {
    return (
      <div className="fmd-coupon-card">
        <p className="fmd-coupon-type">{coupon.label}</p>
        <p style={{ opacity: 0.6, fontSize: '0.9em' }}>No code available</p>
      </div>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fmd-coupon-card">
      <p className="fmd-coupon-type">{coupon.label}</p>

      <div className="fmd-code-box">
        <span className="fmd-code-text">{code}</span>
        <button
          className={`fmd-copy-btn ${copied ? 'fmd-copy-btn--copied' : ''}`}
          onClick={handleCopy}
          aria-label={`Copy ${coupon.label} code`}
        >
          {copied ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
            </svg>
          )}
        </button>
      </div>

      {copied && <p className="fmd-copied-text">Copied!</p>}

      <div className="fmd-validity">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
        <span>Until {coupon.validUntil}</span>
      </div>

      <button
        className="fmd-redeem-btn"
        onClick={() => window.open(coupon.link, '_blank')}
      >
        {coupon.btnLabel}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
          <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
        </svg>
      </button>
    </div>
  );
}

export default function FamilyMemberDetails() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get('id');

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ── Coupon codes fetched from backend ──
  const [coupons, setCoupons] = useState(null);
  const [couponsLoading, setCouponsLoading] = useState(true);

  const handleTakeTest = () => {
    navigate(`/FamilyQuestionnaire?id=${id}`);
  };

  useEffect(() => {
    const fetchMember = async () => {
      if (!id) { setError('No member ID provided.'); setLoading(false); return; }
      try {
        const res = await fetch(`https://backend-coupon-distribution-nhf.onrender.com/family-member/${id}`);
        const data = await res.json();
        if (!res.ok || !data.success) { setError(data.error || 'Member not found.'); return; }
        setMember(data.data);
      } catch {
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchMember();
  }, [id]);

  useEffect(() => {
    const fetchCoupons = async () => {
      if (!id) { setCouponsLoading(false); return; }
      try {
        setCouponsLoading(true);
        const res = await fetch('https://backend-coupon-distribution-nhf.onrender.com/get-family-coupons', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id }),
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setCoupons({ coupon1: data.coupon1, coupon2: data.coupon2 });
        } else {
          setCoupons(null);
        }
      } catch (err) {
        console.error('❌ Error fetching coupons:', err);
        setCoupons(null);
      } finally {
        setCouponsLoading(false);
      }
    };
    fetchCoupons();
  }, [id]);

  if (loading) {
    return (
      <div className="fmd-root">
        <div className="fmd-loading">
          <div className="fmd-spinner" />
          <p>Loading member details...</p>
        </div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="fmd-root">
        <div className="fmd-error-wrap">
          <div className="fmd-error-icon">⚠️</div>
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <button className="fmd-btn-primary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const initials = member.name
    ? member.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  const hasScore = member.score && member.score !== 'NA';

  return (
    <div className="fmd-root">
      <div className="fmd-container fade-in-up">

        {/* Back */}
        <button className="fmd-back" onClick={() => navigate(-1)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>

        {/* Hero */}
        <div className="fmd-hero">
          <div className="fmd-avatar">{initials}</div>
          <h1 className="fmd-name">{member.name}</h1>
          {/* <span className="fmd-status-badge">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Coupon Assigned
          </span> */}
        </div>

         {/* Score */}
        <div className="fmd-score-card">
          {hasScore ? (
            <>
              <p className="fmd-score-heading">Your Test Results</p>
              <div className="fmd-score-row">
                <div className="fmd-score-block">
                  <p className="fmd-score-lbl">Health Score</p>
                  <p className="fmd-score-val">{member.score}</p>
                </div>
                <div className="fmd-score-divider" />
                <div className="fmd-score-block">
                  <p className="fmd-score-lbl">Risk Level</p>
                  <p className={`fmd-risk-val fmd-risk--${member.risk_level?.toLowerCase().replace(' ', '-')}`}>
                    {member.risk_level}
                  </p>
                </div>
              </div>
              <div className="fmd-guide-wrap">
                <p className="fmd-guide-lbl">Score Guide</p>
                <div className="fmd-guide-chips">
                  <div className="fmd-chip fmd-chip--ok">&lt; 5 → Adequate</div>
                  <div className="fmd-chip fmd-chip--bad">≥ 5 → Inadequate</div>
                </div>
              </div>
            </>
          ) : (
            <div className="fmd-no-score">
              <div className="fmd-no-score-top">
                <span className="fmd-no-score-icon">🧪</span>
                <div>
                  <p className="fmd-no-score-title">No Score Yet</p>
                  <p className="fmd-no-score-sub">Take the test to check your vitamin D levels</p>
                </div>
              </div>
              <button className="fmd-btn-take-test"  onClick={handleTakeTest}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                </svg>
                Take the Test
              </button>
            </div>
          )}
        </div>

        {/* Coupons */}
        <p className="fmd-section-label">Your Coupons</p>
        <div className="fmd-coupons-grid">
          {couponsLoading ? (
            <p style={{ opacity: 0.6 }}>Loading coupons...</p>
          ) : (
            COUPONS.map((c) => (
              <CouponCard key={c.key} coupon={c} coupons={coupons} />
            ))
          )}
        </div>

      </div>
    </div>
  );
}